/**
 * 발주서 미리보기 모달 — 저장 후 또는 목록에서 행 클릭 시 표시.
 *
 * Phase A 단계: Excel 다운로드만 활성. PDF / 매입연동 은 다음 패치.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAttachments, previewAttachment, type AttachmentMeta } from '../../lib/attachment-api';
import { isSheetFile } from '../../lib/sheet-preview';
import type { PoDetail, PoLinkedPurchase } from '../../lib/po-api';
import { poPdfDownloadUrl } from '../../lib/po-api';
import { openServerPdfPreview } from '../../lib/pdf-preview';
import { LinkPurchaseFromPoModal } from './LinkPurchaseFromPoModal';
import { mailSubject } from '../../lib/mail-subject';
import { MailSendButton } from '../../components/MailSendButton';
import { LoadingLines } from '../../components/ui/Skeleton';

interface Props {
  open: boolean;
  order: PoDetail | null;
  onClose: () => void;
  /** 제공 시 미리보기 하단에 '✏ 수정' 버튼 표시 (작성 권한자에게만) */
  onEdit?: () => void;
  /**
   * 제공 시 '📋 이 내용으로 새 발주서' 버튼 표시 (v2.213.0 · 작성 권한자에게만 · 견적서 미리보기와 같은 자리).
   * 원본은 건드리지 않고 같은 내용의 새 발주서 작성 폼을 연다.
   */
  onCopy?: () => void;
}

const COMPANY_INFO: Record<string, { name: string; bizNo: string; addr: string; tel: string }> = {
  SM: {
    name: '(주) 에스엠시스템즈',
    bizNo: '113-86-83368',
    addr: '서울 금천구 가산디지털2로 135, A동 1007호 (가산어반워크 1차)',
    tel: 'Tel 070-8708-4777  FAX 070-8708-4775',
  },
  EJ: {
    name: '(주) 이진아이씨티',
    bizNo: '257-86-02520',
    addr: '서울특별시 금천구 가산디지털2로 135, 에이동 10층 1009호(가산동, 가산 어반워크 1차)',
    tel: 'Tel 070-8708-4776  FAX 070-8708-4775',
  },
};

export function PurchaseOrderPreviewModal({ open, order, onClose, onEdit, onCopy }: Props) {
  const [linkOpen, setLinkOpen] = useState(false);
  // 견적서 첨부형의 거래처 견적서 (v2.306.0) — 수정 창 첨부 칸과 같은 캐시(같은 queryKey)라 올리면 바로 보인다
  const quoteFilesQ = useQuery({
    queryKey: ['attachments', 'po', String(order?.id ?? ''), '견적서'],
    queryFn: () => listAttachments('po', String(order!.id), '견적서'),
    enabled: open && !!order && order.poType === 'quote_attached',
  });
  if (!open || !order) return null;
  const co = COMPANY_INFO[order.companyId] ?? COMPANY_INFO.SM!;
  const isQuote = order.poType === 'quote_attached';
  // 📁 옛 발주서(원본 PDF · v2.291.0) — 포털이 그린 발주서 대신 보낸 원본을 그대로 보여 준다
  const isLegacy = order.poType === 'legacy_pdf';
  const hasAmount = order.totalWithVat > 0;
  const openPdf = () => openServerPdfPreview({
    pdfUrl: poPdfDownloadUrl(order.id),
    filename: `발주서_${order.poNo}.pdf`,
    // 옛 발주서는 이미 보낸 원본이라 ✉(다시 보내기)를 달지 않는다
    mailDoc: isLegacy ? undefined : {
      docRefs: [{ docType: 'po', docId: order.id, label: `발주서 ${order.poNo}` }],
      subject: mailSubject('발주서 송부', order.poNo),
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-4xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">발주서 미리보기</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {order.poNo} · {order.empDisplay}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-6 bg-slate-50">
          {isLegacy ? (
          <div className="bg-white border border-amber-300 p-5 text-xs" data-po-legacy>
            <div className="text-[13px] font-semibold text-amber-800">📁 옛 발주서 — 포털을 쓰기 전에 보낸 발주서 PDF 를 그대로 보관한 것입니다</div>
            <div className="mt-1 text-[11px] text-slate-500">내용은 원본 그대로라 고치거나 다시 보낼 수 없습니다. 아래 [📄 원본 PDF 보기]로 보낸 그대로를 봅니다.</div>
            <table className="w-full border-collapse text-xs mt-3">
              <tbody>
                <tr>
                  <Td label>발 주 번 호 :</Td>
                  <Td>{order.poNo}</Td>
                  <Td label>발 주 일 자 :</Td>
                  <Td>{order.poDate}</Td>
                </tr>
                <tr>
                  <Td label>수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;신 :</Td>
                  <Td>{order.recipientSupplier}</Td>
                  <Td label>담&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;당 :</Td>
                  <Td>{order.empDisplay || '-'}</Td>
                </tr>
                <tr>
                  <Td label>프로젝트명 :</Td>
                  <Td colSpan={3}>{order.projectName}</Td>
                </tr>
                <tr>
                  <Td label>발 주 금 액 :</Td>
                  <Td colSpan={3}>
                    <span className="font-bold text-slate-800">{order.totalSupply.toLocaleString()}</span> 원(VAT 별도) ·{' '}
                    {order.totalWithVat.toLocaleString()} 원(VAT 포함)
                  </Td>
                </tr>
              </tbody>
            </table>
            {order.remark && <div className="mt-2 px-2 py-1.5 border border-slate-200 bg-slate-50 whitespace-pre-wrap text-[11px] text-slate-600">{order.remark}</div>}
          </div>
          ) : (
          <div className="bg-white border border-slate-300 p-6 text-xs">
            <div className="text-right text-xs text-slate-700 mb-3 leading-tight">
              <div className="font-bold text-sm">{co.name}</div>
              <div>사업자 등록번호 : {co.bizNo}</div>
              <div>{co.addr}</div>
              <div>{co.tel}</div>
            </div>

            <h1 className="text-center text-lg font-bold tracking-[0.5em] my-4">
              발&nbsp;&nbsp;&nbsp;&nbsp;주&nbsp;&nbsp;&nbsp;&nbsp;서
            </h1>

            <table className="w-full border-collapse text-xs mb-2">
              <tbody>
                <tr>
                  <Td label>발 주 번 호 :</Td>
                  <Td>{order.poNo}</Td>
                  <Td label>발 주 일 자 :</Td>
                  <Td>{order.poDate}</Td>
                </tr>
                <tr>
                  <Td label>수&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;신 :</Td>
                  <Td>{order.recipientSupplier}</Td>
                  <Td label>담&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;당 :</Td>
                  <Td>{order.empDisplay}</Td>
                </tr>
                <tr>
                  <Td label>참&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;조 :</Td>
                  <Td>{order.reference}</Td>
                  <Td label>연  락  처 :</Td>
                  <Td>{order.contactPhone}</Td>
                </tr>
                <tr>
                  <Td label>프로젝트명 :</Td>
                  <Td>{order.projectName}</Td>
                  <Td label>이  메  일 :</Td>
                  <Td>{order.contactEmail}</Td>
                </tr>
                <tr>
                  <Td label>납 품 장 소 :</Td>
                  <Td colSpan={3}>{order.deliveryPlace}</Td>
                </tr>
                <tr>
                  <Td label>납 품 기 한 :</Td>
                  <Td colSpan={3}>{order.deliveryDue}</Td>
                </tr>
                <tr>
                  <Td label>결 제 조 건 :</Td>
                  <Td colSpan={3}>{order.paymentTerm}</Td>
                </tr>
                <tr>
                  <Td label>발 주 금 액 :</Td>
                  <Td colSpan={3}>
                    {isQuote && !hasAmount ? (
                      <span className="font-bold text-slate-800">첨부 견적서 참조</span>
                    ) : (
                      <>
                        <span className="font-bold text-slate-800">
                          {order.totalWithVat.toLocaleString()}
                        </span>{' '}
                        원(VAT 포함)
                      </>
                    )}
                  </Td>
                </tr>
                {(order.linkedSalesIds?.length || order.linkedSalesId) && (
                  <tr>
                    <Td label>관련 영업건 :</Td>
                    {/* 여러 개면 대표 먼저(v2.284.0) */}
                    <Td colSpan={3} className="font-mono">
                      {(order.linkedSalesIds?.length ? order.linkedSalesIds : [order.linkedSalesId!]).join(' · ')}
                      {(order.linkedSalesIds?.length ?? 0) > 1 && <span className="ml-1.5 font-sans text-xs text-slate-500">(첫 번째가 대표)</span>}
                    </Td>
                  </tr>
                )}
              </tbody>
            </table>

            {isQuote ? (
              <div className="border border-slate-300 rounded bg-slate-50 px-4 py-6 text-center">
                <div className="text-[13px] font-semibold text-slate-800">
                  본 발주는 아래 첨부된 거래처 견적서 내용에 따라 발주합니다.
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  ※ PDF 다운로드 시 거래처 견적서(PDF)가 발주서 뒤에 합쳐져 한 파일로 받습니다. (엑셀 견적서는 별도 파일로 함께 첨부)
                </div>
                <QuoteFilesBox files={quoteFilesQ.data} loading={quoteFilesQ.isPending} />
              </div>
            ) : (
            <>
            <div className="text-right text-[11px] text-slate-500 mb-1">단위: 원</div>

            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-100">
                  <th className="border border-slate-300 px-1 py-1 text-center">품목</th>
                  <th className="border border-slate-300 px-1 py-1 text-center">상 세 내 용</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-12">수량</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-20">공급단가</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-20">공급합계</th>
                  <th className="border border-slate-300 px-1 py-1 text-center w-20">비고</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((it) => (
                  <tr key={it.id}>
                    <td className="border border-slate-300 px-1 py-1 align-top">{it.itemName}</td>
                    <td className="border border-slate-300 px-1 py-1 align-top whitespace-pre-wrap">
                      {it.description}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.qty}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.unitPrice.toLocaleString()}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 text-right align-top">
                      {it.lineTotal.toLocaleString()}
                    </td>
                    <td className="border border-slate-300 px-1 py-1 align-top">{it.note}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50">
                  <td colSpan={4} className="border border-slate-300 px-2 py-1 text-right font-medium">
                    공급가 총계 (VAT 별도)
                  </td>
                  <td className="border border-slate-300 px-2 py-1 text-right font-bold">
                    {order.totalSupply.toLocaleString()}
                  </td>
                  <td className="border border-slate-300"></td>
                </tr>
                <tr className="bg-slate-50">
                  <td colSpan={4} className="border border-slate-300 px-2 py-1 text-right font-medium">
                    공급가 총계 (VAT 포함)
                  </td>
                  <td className="border border-slate-300 px-2 py-1 text-right font-bold text-emerald-700">
                    {order.totalWithVat.toLocaleString()}
                  </td>
                  <td className="border border-slate-300"></td>
                </tr>
              </tbody>
            </table>
            </>
            )}

            <div className="mt-2 border border-slate-300">
              <div className="bg-slate-50 px-2 py-1 border-b border-slate-300 font-medium">
                Remark
              </div>
              <div className="px-2 py-1.5 min-h-[3rem] whitespace-pre-wrap">{order.remark}</div>
            </div>

            <div className="mt-3 text-[11px] text-slate-600 leading-relaxed">
              <div>1. 별도 계약서가 없을 시 본 발주서가 계약서를 대신하며 발주서에 기입된 납품일자를 준수한다.</div>
              <div>2. 납기 이행을 위반시에는 지연 1일당 납품 금액에 대한 5/1000 에 해당하는 지체상금을 지불해야 한다.</div>
              <div>3. 물품의 공급되는 시기는 당사가 정하는 시간과 장소에 납품하며 검사에 의한 합격품에 한하여 계산서를 발행한다.</div>
            </div>
          </div>
          )}

          {/* 📄 이 발주서로 등록된 매입 — 나뉜 줄·손으로 이은 줄 포함 (v2.287.0) */}
          <LinkedPurchasesBox rows={order.linkedPurchases ?? []} isQuote={isQuote} />
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="btn btn-warning-solid"
              >
                ✏ 수정
              </button>
            )}
            {onCopy && (
              <button
                type="button"
                onClick={onCopy}
                title="이 발주서 내용을 그대로 가져와 새 발주서를 작성합니다 (원본은 그대로 남습니다)"
                className="btn btn-primary"
              >
                📋 이 내용으로 새 발주서
              </button>
            )}
            {!isQuote && !isLegacy && (
              <button
                type="button"
                onClick={() => setLinkOpen(true)}
                className="btn btn-primary"
              >
                🔗 매입 연동 ({order.items.length}건)
              </button>
            )}
            <button
              type="button"
              onClick={openPdf}
              className="btn btn-danger-solid"
            >
              {isLegacy ? '📄 원본 PDF 보기' : '📄 PDF 미리보기'}
            </button>
            {/* ✉ 메일 — **여기서 바로** 보낸다 (v2.175.0 · 사용자 요청 2026-09-14:
                「발주서 미리보기 상태에서 메일보내기 버튼 만들기」).
                ⭐PDF 미리보기 창의 ✉ 는 **그대로 둔다** — 둘 다 되는 것이지 옮긴 것이 아니다.
                발주서는 형식이 PDF 하나뿐이라 ▾ 없이 바로 작성창이 뜬다.
                viewer 는 MailSendButton 이 스스로 안 그린다. */}
            {!isLegacy && <MailSendButton
              compose={() => ({
                docRefs: [{ docType: 'po', docId: order.id, label: `발주서 ${order.poNo}` }],
                subject: mailSubject('발주서 송부', order.poNo),
              })}
              menuDirection="up"
              label="✉ 메일로 보내기"
              title="이 발주서를 첨부한 메일을 바로 작성합니다"
              className="px-3 py-1.5 text-sm bg-sky-600 hover:bg-sky-700 text-white rounded"
            />}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
      <LinkPurchaseFromPoModal
        open={linkOpen}
        order={order}
        onClose={() => setLinkOpen(false)}
      />
    </div>
  );
}

/** 연결이 어떻게 생겼나(purchase_order_purchase.how) → 사람 말 */
const HOW_LABEL: Record<string, string> = { 연동: '매입 연동', 상속: '나뉜 줄', 수동: '직접 연결', 정리: '비고로 맞춤', 대조: '발주서 PDF 대조' };

/** 발주서 미리보기 아래 「이 발주서로 등록된 매입 N줄」 — 어느 영업건으로 갔고 입고·출고됐는지 */
function LinkedPurchasesBox({ rows, isQuote }: { rows: PoLinkedPurchase[]; isQuote: boolean }) {
  return (
    <div className="mt-4 border border-slate-300 bg-white" data-po-linked>
      <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-300 text-xs font-semibold text-slate-700">
        이 발주서로 등록된 매입 {rows.length}줄
        <span className="ml-2 font-normal text-slate-500">매입관리에서 이 번호로 찾을 수 있습니다 · 나뉜 줄도 따라옵니다</span>
      </div>
      {rows.length === 0 ? (
        isQuote ? (
          // 견적서 첨부형은 품목 표가 없어 [🔗 매입 연동] 단추가 없다 (v2.306.0 · 챗봇이 「권한이 막혔다」고 잘못 안내했던 자리)
          <div className="px-3 py-3 text-xs text-slate-500" data-po-quote-link-hint>
            아직 없습니다 — <b>견적서 첨부형</b>은 품목 표가 없어 [🔗 매입 연동] 단추가 없습니다.
            🛒 매입관리에서 [+ 매입 등록]으로 품목을 넣은 뒤, 그 매입의 수정 창 [발주서 고르기]로 이 발주서를 이어 주세요.
          </div>
        ) : (
          <div className="px-3 py-3 text-xs text-slate-400">아직 없습니다 — [🔗 매입 연동]으로 등록하거나, 매입 수정 창에서 [발주서 고르기]로 이을 수 있습니다.</div>
        )
      ) : (
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="text-left px-3 py-1 font-medium">품목</th>
              <th className="text-right px-2 py-1 font-medium">수량</th>
              <th className="text-left px-2 py-1 font-medium">영업건</th>
              <th className="text-left px-2 py-1 font-medium">입고</th>
              <th className="text-left px-2 py-1 font-medium">출고</th>
              <th className="text-left px-2 py-1 font-medium">연결</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.purchaseId} className="border-t border-slate-100">
                <td className="px-3 py-1">
                  <span className="text-slate-400 mr-1">#{r.purchaseId}</span>
                  {r.itemName ?? r.description ?? '-'}
                  {r.itemName && r.description && <span className="text-slate-400"> · {r.description}</span>}
                </td>
                <td className="px-2 py-1 text-right num">{r.orderQty}</td>
                <td className="px-2 py-1 whitespace-nowrap">{r.salesId ? <>{r.salesId}{r.salesEndUser && <span className="text-slate-400"> · {r.salesEndUser}</span>}</> : <span className="text-slate-400">재고 보유</span>}</td>
                <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{r.actualInDate ?? '-'}</td>
                <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{r.actualOutDate ?? '-'}</td>
                <td className="px-2 py-1 text-slate-500 whitespace-nowrap">{HOW_LABEL[r.how] ?? r.how}{r.parentPurchaseId != null && <span className="text-slate-400"> (#{r.parentPurchaseId}에서)</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * 견적서 첨부형 발주서의 거래처 견적서 목록 (v2.306.0) — 👁 로 그 자리에서 본다(엑셀은 표로 · v2.270.0 미리보기).
 *   권애영 님(챗봇 대화 #46·#49 · 10-23): 「엑셀로 첨부되면 메일로 보내기 전에 더블체크하기가 어렵다」 —
 *   PDF 미리보기에는 PDF 견적서만 합쳐지고 엑셀은 별도 파일이라, 전에는 내려받아 열어야 했다.
 */
function QuoteFilesBox({ files, loading }: { files: AttachmentMeta[] | undefined; loading: boolean }) {
  return (
    <div className="mt-3 text-left border-t border-slate-200 pt-2" data-po-quote-files>
      <div className="text-[11px] font-semibold text-slate-600 mb-1">
        첨부 견적서 {files ? `${files.length}개` : ''}
        <span className="ml-1 font-normal text-slate-500">— 보내기 전에 👁 로 내용을 확인하세요 (엑셀도 표로 보입니다)</span>
      </div>
      {loading ? <div className="text-[11px] text-slate-400"><LoadingLines /></div> : null}
      {files && files.length === 0 ? (
        <div className="text-[11px] text-amber-700">첨부된 견적서가 없습니다 — [✏ 수정]에서 거래처 견적서를 올려 주세요.</div>
      ) : null}
      {(files ?? []).map((a) => (
        <div key={a.id} className="flex items-center gap-2 py-0.5 text-[12px]">
          <button type="button" onClick={() => previewAttachment(a)} className="btn btn-sm btn-secondary" title={`${a.filename} 미리보기`}>
            👁 미리보기
          </button>
          <span className="truncate text-slate-800" title={a.filename}>{a.filename}</span>
          <span className="shrink-0 text-slate-400">
            {isSheetFile(a.filename, a.mime_type) ? '엑셀 · 메일에 별도 파일로' : 'PDF · 발주서 뒤에 합쳐짐'}
          </span>
        </div>
      ))}
    </div>
  );
}

function Td({
  children,
  label,
  colSpan,
  className,
}: {
  children?: React.ReactNode;
  label?: boolean;
  colSpan?: number;
  className?: string;
}) {
  return (
    <td
      colSpan={colSpan}
      className={
        'border border-slate-300 px-2 py-1 ' +
        (label ? 'bg-slate-50 font-medium text-slate-600 w-28 whitespace-nowrap' : 'text-slate-800') +
        (className ? ' ' + className : '')
      }
    >
      {children}
    </td>
  );
}
